/**
 * Lightweight copyright protection for storefront images and content.
 * Shows a warning when visitors try to copy, download, or inspect assets.
 * Disabled in Shopify theme editor / design mode.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.Shopify && window.Shopify.designMode) return;

  const WARNING_TITLE = 'Warning';
  const WARNING_MESSAGE =
    'This artwork and content are protected by copyright. Downloading, copying, or saving assets without permission is prohibited.';

  let hideTimer = null;

  function isEditableTarget(target) {
    if (!target || !(target instanceof Element)) return false;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (target.isContentEditable) return true;
    return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
  }

  function isImageTarget(target) {
    if (!target || !(target instanceof Element)) return false;
    if (target.tagName === 'IMG' || target.tagName === 'PICTURE' || target.tagName === 'SVG') return true;
    if (target.closest('img, picture, svg, .media, .product__media, .card__media')) return true;
    const style = window.getComputedStyle(target);
    return style && style.backgroundImage && style.backgroundImage !== 'none';
  }

  function ensureWarningElement() {
    let warning = document.getElementById('copyright-protection-warning');
    if (warning) return warning;

    warning = document.createElement('div');
    warning.id = 'copyright-protection-warning';
    warning.setAttribute('role', 'alert');
    warning.setAttribute('aria-live', 'assertive');
    warning.innerHTML =
      '<div class="copyright-protection-warning__panel">' +
      '<p class="copyright-protection-warning__title"></p>' +
      '<p class="copyright-protection-warning__message"></p>' +
      '</div>';
    warning.querySelector('.copyright-protection-warning__title').textContent = WARNING_TITLE;
    warning.querySelector('.copyright-protection-warning__message').textContent = WARNING_MESSAGE;
    document.body.appendChild(warning);
    return warning;
  }

  function showWarning() {
    try {
      const warning = ensureWarningElement();
      warning.classList.add('is-visible');

      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        warning.classList.remove('is-visible');
      }, 3200);
    } catch (e) {
      // no-op
    }
  }

  function injectStyles() {
    if (document.getElementById('copyright-protection-styles')) return;
    const style = document.createElement('style');
    style.id = 'copyright-protection-styles';
    style.textContent = `
      img, picture, video, svg, canvas,
      .media, .product__media, .card__media, .banner__media {
        -webkit-user-drag: none;
        user-drag: none;
        -webkit-user-select: none;
        user-select: none;
      }
      body.copyright-protection--active {
        -webkit-touch-callout: none;
      }
      body.copyright-protection--active :not(input):not(textarea):not(select):not([contenteditable="true"]) {
        -webkit-user-select: none;
        user-select: none;
      }
      body.copyright-protection--active input,
      body.copyright-protection--active textarea,
      body.copyright-protection--active select,
      body.copyright-protection--active [contenteditable="true"] {
        -webkit-user-select: text;
        user-select: text;
      }
      #copyright-protection-warning {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.25rem;
        background: rgba(0, 0, 0, 0.45);
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.2s ease, visibility 0.2s ease;
      }
      #copyright-protection-warning.is-visible {
        opacity: 1;
        visibility: visible;
      }
      .copyright-protection-warning__panel {
        max-width: 28rem;
        width: 100%;
        padding: 1.25rem 1.5rem;
        border-radius: 6px;
        border-left: 4px solid #c9a227;
        background: #1a1a1a;
        color: #fff;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
        text-align: left;
      }
      .copyright-protection-warning__title {
        margin: 0 0 0.4rem;
        font-size: 1rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #c9a227;
      }
      .copyright-protection-warning__message {
        margin: 0;
        font-size: 0.95rem;
        line-height: 1.45;
        color: rgba(255, 255, 255, 0.92);
      }
    `;
    document.head.appendChild(style);
  }

  function disableImageDragging() {
    document.querySelectorAll('img').forEach((img) => {
      img.setAttribute('draggable', 'false');
      img.addEventListener('dragstart', (event) => {
        event.preventDefault();
        showWarning();
      });
    });
  }

  function onContextMenu(event) {
    if (isEditableTarget(event.target)) return;
    event.preventDefault();
    showWarning();
  }

  function onDragStart(event) {
    if (isImageTarget(event.target)) {
      event.preventDefault();
      showWarning();
    }
  }

  function onCopyCut(event) {
    if (isEditableTarget(event.target)) return;
    event.preventDefault();
    showWarning();
  }

  function onKeyDown(event) {
    if (isEditableTarget(event.target)) return;

    const key = event.key ? event.key.toLowerCase() : '';
    const ctrlOrMeta = event.ctrlKey || event.metaKey;

    // Block common save / view-source / print / select-all / copy shortcuts
    if (
      ctrlOrMeta &&
      (key === 's' || key === 'u' || key === 'p' || key === 'a' || key === 'c' || key === 'x')
    ) {
      event.preventDefault();
      showWarning();
      return;
    }

    // Block Print Screen where browsers allow it
    if (key === 'printscreen') {
      event.preventDefault();
      showWarning();
    }
  }

  function init() {
    injectStyles();
    document.body.classList.add('copyright-protection--active');
    disableImageDragging();

    document.addEventListener('contextmenu', onContextMenu, { capture: true });
    document.addEventListener('dragstart', onDragStart, { capture: true });
    document.addEventListener('copy', onCopyCut, { capture: true });
    document.addEventListener('cut', onCopyCut, { capture: true });
    document.addEventListener('keydown', onKeyDown, { capture: true });

    // Re-apply to images added dynamically (e.g. quick view, galleries)
    const observer = new MutationObserver(() => disableImageDragging());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
